import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", description: "", price_eur: "", modules: [], active: true };

const PlanForm = ({ plan, modules, onSaved, onCancel }) => {
  const [f, setF] = useState(plan ? { ...plan } : EMPTY);
  const [busy, setBusy] = useState(false);
  const toggle = (m) => setF((x) => ({ ...x, modules: x.modules.includes(m) ? x.modules.filter((k) => k !== m) : [...x.modules, m] }));
  const save = async (e) => {
    e.preventDefault();
    if (plan && Number(f.price_eur) !== plan.price_eur && plan.subscribers
      && !window.confirm(`Le nouveau prix s'appliquera aux ${plan.subscribers} abonné(s) de cette formule à leur prochain renouvellement. Continuer ?`)) return;
    setBusy(true);
    try {
      const body = { ...f, price_eur: Number(f.price_eur) };
      const { data } = plan ? await api.put(`/superadmin/plans/${plan.plan_id}`, body) : await api.post("/superadmin/plans", body);
      toast.success(plan ? `Formule enregistrée${data.migrated ? ` · ${data.migrated} abonnement(s) passeront au nouveau prix au prochain renouvellement` : ""}` : "Formule créée dans Stripe");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const tid = plan?.plan_id || "new";
  return (
    <form onSubmit={save} className="rounded-lg border border-blue-200 bg-blue-50/40 p-5 space-y-4" data-testid={`plan-form-${tid}`}>
      <div className="grid sm:grid-cols-[1fr_160px] gap-3">
        <Input placeholder="Nom de la formule (ex. Essentiel)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid={`plan-name-${tid}`} />
        <Input type="number" step="0.01" min="1" placeholder="Prix HT / technicien / mois" value={f.price_eur} onChange={(e) => setF({ ...f, price_eur: e.target.value })} required data-testid={`plan-price-${tid}`} />
      </div>
      <Input placeholder="Description courte affichée au client" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`plan-desc-${tid}`} />
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-500">Modules inclus (la prise en main à distance est toujours incluse)</p>
        {Object.entries(modules).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.modules.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`plan-module-${tid}-${k}`} />{label}</label>
        ))}
      </div>
      {plan && !plan.is_default && <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.active} onCheckedChange={(v) => setF({ ...f, active: !!v })} data-testid={`plan-active-${tid}`} />Proposée aux clients</label>}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} data-testid={`plan-save-${tid}`}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Enregistrer</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

export const PlansPanel = () => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/superadmin/plans").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const done = () => { setEdit(null); load(); };
  const remove = (p) => window.confirm(`Supprimer la formule « ${p.name} » ?`) && api.delete(`/superadmin/plans/${p.plan_id}`)
    .then(() => { toast.success("Formule supprimée"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4 max-w-3xl" data-testid="sa-plans">
      <p className="text-sm text-slate-500">Prix HT par technicien et par mois. Un changement de prix s'applique à tous les abonnés de la formule à leur prochain renouvellement. Pendant l'essai gratuit, tous les modules sont ouverts.</p>
      {d.plans.map((p) => edit === p.plan_id ? <PlanForm key={p.plan_id} plan={p} modules={d.modules} onSaved={done} onCancel={() => setEdit(null)} /> : (
        <div key={p.plan_id} className="rounded-lg border border-slate-200 p-5 flex items-start gap-4" data-testid={`plan-${p.plan_id}`}>
          <div className="flex-1">
            <p className="font-semibold">{p.name} {p.is_default && <Badge variant="outline" className="ml-1">Par défaut</Badge>} {!p.active && <Badge className="ml-1 bg-slate-200 text-slate-600 hover:bg-slate-200">Masquée</Badge>}</p>
            <p className="text-sm text-slate-500">{p.description}</p>
            <p className="text-xs text-slate-500 mt-2">Prise en main à distance{p.modules.map((m) => ` · ${d.modules[m]?.split(" (")[0]}`).join("")}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold">{p.price_eur.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
            <p className="text-xs text-slate-500">{p.subscribers} abonné(s)</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setEdit(p.plan_id)} data-testid={`plan-edit-${p.plan_id}`}>Modifier</Button>
            {!p.is_default && !p.subscribers && <Button size="sm" variant="ghost" className="mt-2 ml-1 text-red-600" onClick={() => remove(p)} data-testid={`plan-delete-${p.plan_id}`}>Supprimer</Button>}
          </div>
        </div>
      ))}
      {edit === "new" ? <PlanForm modules={d.modules} onSaved={done} onCancel={() => setEdit(null)} /> : (
        <Button variant="outline" onClick={() => setEdit("new")} data-testid="plan-new-btn"><Plus className="h-4 w-4 mr-2" />Nouvelle formule</Button>
      )}
    </div>
  );
};
